import { ref, onMounted, onBeforeUnmount } from 'vue'
import { fromEvent, merge, Subject, Subscription } from 'rxjs'
import { debounceTime } from 'rxjs/operators'

export function useSelect(externalTarget?: any) {
  const selectBoxStyle = ref<Record<string, any>>({})
  const target = externalTarget || ref<HTMLElement | null>(null)
  const customWidth = ref<number | undefined>(undefined)
  const customSpacing = ref<number | undefined>(undefined)
  const customZIndex = ref<number | undefined>(undefined)
  const customPosition = ref<'left' | 'right' | undefined>(undefined)
  const customDirection = ref<'vertical' | 'horizontal' | undefined>(undefined)
  const customHeight = ref<number | undefined>(undefined)
  const autoWidth = ref<boolean>(false)

  const DEFAULT_BOX_HEIGHT = 300
  const DEFAULT_Z_INDEX = 100
  const DEFAULT_SPACING = 8

  let eventsSubscription: Subscription | null = null
  const intersection$ = new Subject<void>()
  let observer: IntersectionObserver | null = null

  const setCustomSelectConfig = (config: {
    width?: number
    spacing?: number
    zIndex?: number
    position?: 'left' | 'right'
    direction?: 'vertical' | 'horizontal'
    height?: number
    autoWidth?: boolean
  }) => {
    if (config.width) customWidth.value = config.width
    if (config.spacing) customSpacing.value = config.spacing
    if (config.zIndex) customZIndex.value = config.zIndex
    if (config.position) customPosition.value = config.position
    if (config.direction) customDirection.value = config.direction
    if (config.height) customHeight.value = config.height
    if (config.autoWidth !== undefined) autoWidth.value = config.autoWidth
  }

  const updateSelectBoxStyle = (targetElement: HTMLElement | null) => {
    if (!targetElement) return

    target.value = targetElement
    const rect = targetElement.getBoundingClientRect()
    const spaceAbove = rect.top
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceLeft = rect.left
    const spaceRight = window.innerWidth - rect.right
    const spacing = customSpacing.value ?? DEFAULT_SPACING
    let top = 'auto',
      bottom = 'auto',
      left = 'auto',
      right = 'auto'

    // autoWidth가 true일 때는 width 계산을 건너뜀, 그렇지 않으면 기존 로직 사용
    const width = customWidth.value || rect.width
    const useAutoWidth = autoWidth.value

    if (customDirection.value === 'vertical') {
      if (spaceBelow >= DEFAULT_BOX_HEIGHT || spaceBelow >= spaceAbove) {
        top = `${rect.bottom + spacing}px`
        left = `${rect.left}px`
      } else {
        bottom = `${window.innerHeight - rect.top + spacing}px`
        left = `${rect.left}px`
      }
    } else if (customDirection.value === 'horizontal') {
      if (useAutoWidth || spaceRight >= width || spaceRight >= spaceLeft) {
        top = `${rect.top}px`
        left = `${rect.right + spacing}px`
      } else {
        top = `${rect.top}px`
        right = `${window.innerWidth - rect.left + spacing}px`
      }
    } else {
      if (spaceBelow < DEFAULT_BOX_HEIGHT && spaceAbove > DEFAULT_BOX_HEIGHT) {
        bottom = `${window.innerHeight - rect.top + spacing}px`
        left = `${rect.left}px`
      } else {
        top = `${rect.bottom + spacing}px`
        left = `${rect.left}px`
      }
    }

    if (customPosition.value === 'right' && !useAutoWidth) {
      left = `${rect.left - (width - rect.width)}px`
    }

    selectBoxStyle.value = {
      position: 'fixed',
      top,
      bottom,
      left,
      right,
      width: useAutoWidth ? 'auto' : `${width}px`,
      zIndex: customZIndex.value ?? DEFAULT_Z_INDEX
    }

    if (customHeight.value) {
      selectBoxStyle.value.maxHeight = customHeight.value + 'px'
    }

    observeDropdownPosition(targetElement)
  }

  const observerCallback: IntersectionObserverCallback = (entries) => {
    if (entries.some((entry) => !entry.isIntersecting)) {
      intersection$.next()
    }
  }

  const observeDropdownPosition = (targetElement: HTMLElement | null) => {
    if (observer) observer.disconnect()
    if (targetElement) {
      observer = new IntersectionObserver(observerCallback, { root: document.body })
      observer.observe(targetElement)
    }
  }

  onMounted(() => {
    const resize$ = fromEvent(window, 'resize')
    const scroll$ = fromEvent(window, 'scroll')

    eventsSubscription = merge(resize$, scroll$, intersection$)
      .pipe(debounceTime(300))
      .subscribe(() => {
        if (target.value) {
          updateSelectBoxStyle(target.value)
        }
      })
  })

  onBeforeUnmount(() => {
    if (eventsSubscription) eventsSubscription.unsubscribe()
    if (observer) observer.disconnect()
  })

  return {
    selectBoxStyle,
    setCustomSelectConfig,
    updateSelectBoxStyle
  }
}
